import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import ReactPaginate from "react-paginate";
import { useSelector } from "react-redux";
import Loading from "../common/Loading";
import OrderDetailsPopup from "../common/OrderDetailsPopup";
import {
  getCurrencySymbol,
  getItemDetail,
  getPaymentGateway,
  isOnlineRefundSupported,
  getGatewayDisplayName,
} from "../../helper/_commonHelper";
import { commonSelector } from "../../store/slices/common/commonSlice";

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  return `${d.toDateString()} ${d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const getNewDate = () => {
  const tDate = moment().format("DD/MM/YYYY").split("/");
  return `${tDate[2]}-${tDate[1]}-${tDate[0]}`;
};

// Pick the online payment row (QPAY or CyberSource) and its refunds. Falls
// back to the first payment row so the table still renders something
// reasonable when the gateway isn't known yet.
const getOnlinePayment = (order) => {
  const payments = order?.order_payments || [];
  return (
    payments.find((p) => isOnlineRefundSupported(p?.gateway)) ||
    payments[0] ||
    {}
  );
};

const Refunds = () => {
  const formatedDate = getNewDate();
  const { userProfile } = useSelector(commonSelector);
  const hotelCurrencyCode = userProfile.hotel?.country?.currency_code || "";
  const currencySymbol = getCurrencySymbol(hotelCurrencyCode);

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [pageNumber, setpageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(25);
  const [maxPageLimit, setMaxPageLimit] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState({
    date_start: formatedDate,
    date_end: formatedDate,
    search: "",
  });

  useEffect(() => {
    fetchRefundedOrders();
  }, [pageNumber, pageCount, filters]);

  const fetchRefundedOrders = async () => {
    setOrders([]);
    try {
      setLoading(true);
      let filterParams = "";
      Object.keys(filters).forEach((k) => {
        if (filters[k] !== "" && filters[k] !== undefined && filters[k] !== null) {
          filterParams += `&${k}=${encodeURIComponent(filters[k])}`;
        }
      });
      const response = await axios.get(
        `/outlet/order/history?page=${pageNumber}&per_page=${pageCount}&refunded=true${filterParams}`
      );
      if (response.status === 200) {
        const data = response.data.data || {};
        setMaxPageLimit(data.last_page || 0);
        setTotalOrders(data.total || 0);
        const onlineOrders = (data?.data || [])
          .filter((order) => {
            const gateway = getPaymentGateway(order);
            const isPaidOnline = order?.payment_status === "done";
            return isOnlineRefundSupported(gateway) && isPaidOnline;
          })
          .sort((a, b) => b.id - a.id)
          .map((x) => ({
            ...x,
            calculatedData: getItemDetail(x),
          }));
        setOrders(onlineOrders);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setpageNumber(1);
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    setpageNumber(1);
    fetchRefundedOrders();
  };

  const clearFilters = () => {
    setFilters({
      date_start: formatedDate,
      date_end: formatedDate,
      search: "",
    });
    setpageNumber(1);
  };

  const handleOpenDetails = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  return (
    <main className="order-history-wrapper revenue_wrapper refunds-wrapper">
      <div className="aside-left">
        <div className="revenue_title">Refund filters</div>
        <div className="revenue_main">
          <div className="dates_wrapper">
            <div className="form_group">
              <label htmlFor="date_start">Start Date:</label>
              <input
                type="date"
                id="date_start"
                name="date_start"
                value={filters.date_start}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form_group">
              <label htmlFor="date_end">End Date:</label>
              <input
                type="date"
                id="date_end"
                name="date_end"
                value={filters.date_end}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form_group">
              <label htmlFor="search">Search by Name/Mobile/Order ID</label>
              <input
                type="text"
                id="search"
                name="search"
                placeholder="Search by Name/Mobile/Order Id"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form_group">
              <div style={{ display: "flex" }}>
                <button
                  className="btn btn-submit"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </button>
                <button
                  className="btn btn-submit"
                  onClick={clearFilters}
                  style={{ flex: 1 }}
                >
                  Clear filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="aside-right">
        <div className="container-fluid">
          <div className="filter-container">
            <h3 className="refunds-title">Online Payment Refunds</h3>
          </div>
          <div
            className="row home-order-screen"
            style={{ position: "relative" }}
          >
            <table className="table table-striped table-hover table-history table-refunds">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Gateway</th>
                  <th>Payment Order ID</th>
                  <th>Paid Amount</th>
                  <th>Payment Status</th>
                  <th>Refund Status</th>
                  <th>Refunded Amount</th>
                  <th>Refund PUN</th>
                  <th>Initiated By</th>
                  <th>Initiated At</th>
                  <th>Completed At</th>
                  <th>Gateway Message</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={13} className="refunds-state-cell">
                      <Loading />
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="refunds-state-cell empty">
                      No online (QPAY / CyberSource) orders found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => {
                    const payment = getOnlinePayment(order);
                    const refunds = payment?.refunds || [];
                    const refundRows = refunds.length
                      ? refunds
                      : [
                          {
                            id: `placeholder-${order.id}`,
                            placeholder: true,
                          },
                        ];
                    return refundRows.map((refund, idx) => (
                      <tr
                        key={`${order.id}-${refund.id}`}
                        className="cursor-point"
                        onClick={() => handleOpenDetails(order)}
                      >
                        {idx === 0 ? (
                          <>
                            <td rowSpan={refundRows.length}>#{order.id}</td>
                            <td rowSpan={refundRows.length}>
                              <span
                                className={`gateway-badge gateway-${String(
                                  payment?.gateway || ""
                                ).toLowerCase()}`}
                              >
                                {getGatewayDisplayName(payment?.gateway)}
                              </span>
                            </td>
                            <td rowSpan={refundRows.length}>
                              {payment?.payment_order_id || "--"}
                            </td>
                            <td rowSpan={refundRows.length}>
                              {currencySymbol} {payment?.amount ?? "--"}
                            </td>
                            <td rowSpan={refundRows.length}>
                              <span
                                className={`refund-status-badge payment-${
                                  payment?.status || "unknown"
                                }`}
                              >
                                {payment?.status || "--"}
                              </span>
                            </td>
                          </>
                        ) : null}
                        {refund.placeholder ? (
                          <td colSpan={7}>
                            <span style={{ fontStyle: "italic" }}>
                              No refunds initiated
                            </span>
                          </td>
                        ) : (
                          <>
                            <td>
                              <span
                                className={`refund-status-badge refund-${refund.status}`}
                              >
                                {refund.status}
                              </span>
                            </td>
                            <td>
                              {currencySymbol} {refund.amount}
                            </td>
                            <td>{refund.refund_pun || "--"}</td>
                            <td>
                              {refund.refunded_by ||
                                refund.initiator?.name ||
                                refund.initiated_by_user_id ||
                                "--"}
                            </td>
                            <td>{formatDateTime(refund.initiated_at)}</td>
                            <td>{formatDateTime(refund.completed_at)}</td>
                            <td>{refund.gateway_status_message || "--"}</td>
                          </>
                        )}
                        {idx === 0 ? (
                          <td rowSpan={refundRows.length}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDetails(order);
                              }}
                            >
                              View
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ));
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="pagination-container">
            <div className="totalcount">
              <label>Total Orders: </label>{" "}
              <label>
                <b>{totalOrders}</b>
              </label>
            </div>
            <div className="form-group1">
              <label>Row per Page: </label>
              <select
                name="count"
                id="count"
                value={pageCount}
                onChange={(e) => setPageCount(Number(e.target.value))}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="75">75</option>
                <option value="100">100</option>
              </select>
            </div>
            <ReactPaginate
              previousLabel={"Previous"}
              nextLabel={"Next"}
              breakLabel={"..."}
              breakClassName={"break-me"}
              pageCount={maxPageLimit}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={(e) => setpageNumber(e.selected + 1)}
              containerClassName={"pagination"}
              subContainerClassName={"pages pagination"}
              activeClassName={"active"}
            />
          </div>
        </div>
      </div>
      {isModalOpen && selectedItem && (
        <OrderDetailsPopup
          item={selectedItem}
          open={isModalOpen}
          close={() => setIsModalOpen(false)}
        />
      )}
    </main>
  );
};

export default Refunds;
